import type { Channel, SessionData } from '@tla/types';
import { getChannel } from './laps.js';

/** Deceleration (G) treated as 100% brake for the derived brake channel. */
const FULL_BRAKE_G = 1.0;
/** Throttle percentage below which we consider the driver off-throttle. */
const OFF_THROTTLE_PCT = 20;

/**
 * Compute a derived brake channel (0..100%) from longitudinal deceleration,
 * gated by throttle position when available. RaceChrono logs no brake pressure,
 * so braking is inferred: brake = clamp(-longitudinal_g / FULL_BRAKE_G) while the
 * driver is off-throttle.
 *
 * Returns undefined if longitudinal acceleration is unavailable.
 */
export function computeBrakeChannel(session: SessionData): Channel | undefined {
  const lon = getChannel(session, 'longitudinal_acc');
  if (!lon) return undefined;
  const throttle = getChannel(session, 'throttle_pos');

  const n = lon.samples.length;
  const out = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const g = lon.samples[i];
    const thr = throttle ? throttle.samples[i] : NaN;
    if (!Number.isFinite(g) || g >= 0) {
      out[i] = 0;
      continue;
    }
    // If throttle is known and the driver is on-throttle, not braking.
    if (Number.isFinite(thr) && thr > OFF_THROTTLE_PCT) {
      out[i] = 0;
      continue;
    }
    const pct = Math.min(100, (-g / FULL_BRAKE_G) * 100);
    out[i] = pct;
  }

  return {
    id: 'brake',
    name: 'Brake (est.)',
    unit: '%',
    group: 'Derived',
    source: 'derived',
    samples: out,
    derived: true,
  };
}

/**
 * Attach all derived channels to a session (currently the estimated brake
 * channel). Idempotent: existing derived channels with the same id are replaced.
 */
export function withDerivedChannels(session: SessionData): SessionData {
  const derived: Channel[] = [];
  const brake = computeBrakeChannel(session);
  if (brake) derived.push(brake);

  const derivedIds = new Set(derived.map((c) => c.id));
  const base = session.channels.filter((c) => !derivedIds.has(c.id));
  return { ...session, channels: [...base, ...derived] };
}
